import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API } from "@/lib/api";
import { Calendar, Video, ExternalLink, CheckCircle2, Loader2, AlertCircle, ShieldCheck } from "lucide-react";
import { track, EVENTS } from "@/lib/analytics";

/**
 * /book/success — Stripe redirect target. We poll the backend until
 * payment_status === "paid", then reveal the Calendly URL (so the user can
 * pick their time) and the Doxy.me HIPAA video room. Only the BACKEND
 * decides whether the URLs are revealed — the frontend just shows what it
 * was given.
 *
 * Polling stops on success, expiration, or after 10 attempts (~20s).
 */
const MAX_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export default function BookingSuccess() {
    const [params] = useSearchParams();
    const sid = params.get("session_id") || "";
    const [state, setState] = useState({ phase: "checking", data: null, error: null });
    const cancelledRef = useRef(false);

    useEffect(() => {
        cancelledRef.current = false;
        if (!sid) {
            setState({ phase: "error", data: null, error: "missing_session" });
            return;
        }

        const poll = async (attempt = 0) => {
            if (cancelledRef.current) return;
            try {
                const res = await fetch(`${API}/booking/status/${sid}`);
                const data = await res.json();
                if (!res.ok) throw new Error(data.detail || "status_failed");
                if (data.paid) {
                    setState({ phase: "paid", data, error: null });
                    // Fire once per Stripe session id — a refresh of the
                    // success page should not double-count the booking.
                    if (typeof window !== "undefined"
                        && !sessionStorage.getItem(`ml_ga_booked_${sid}`)) {
                        sessionStorage.setItem(`ml_ga_booked_${sid}`, "1");
                        const bookingParams = {
                            stripe_session_id: sid,
                            amount: data.amount,
                            currency: data.currency,
                        };
                        // New investor-grade event name + legacy alias so
                        // existing GA4 dashboards keep working.
                        track(EVENTS.CALENDLY_BOOKING, bookingParams);
                        track(EVENTS.CARE_NAVIGATOR_SESSION_BOOKED, bookingParams);
                        // GA4 standard "purchase" — counts toward revenue
                        // reports out-of-the-box.
                        track(EVENTS.PURCHASE, {
                            transaction_id: sid,
                            value: typeof data.amount === "number" ? data.amount / 100 : undefined,
                            currency: data.currency,
                        });
                    }
                    return;
                }
                if ((data.stripe_status || "").includes("expired")) {
                    setState({ phase: "expired", data, error: null });
                    return;
                }
                if (attempt >= MAX_ATTEMPTS) {
                    setState({ phase: "timeout", data, error: null });
                    return;
                }
                setState({ phase: "pending", data, error: null });
                setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
            } catch (e) {
                setState({ phase: "error", data: null, error: String(e?.message || e) });
            }
        };
        poll(0);

        return () => { cancelledRef.current = true; };
    }, [sid]);

    return (
        <div className="max-w-2xl mx-auto px-4 py-12 md:py-20 animate-fade-in-up" data-testid="booking-success-page">
            {state.phase === "checking" || state.phase === "pending" ? (
                <article className="ml-card p-8 text-center" data-testid="booking-status-pending">
                    <Loader2 size={32} strokeWidth={1.8} className="mx-auto animate-spin text-teal-dark" />
                    <h1 className="font-heading text-2xl mt-4 text-[#12776F]">Confirming your payment…</h1>
                    <p className="mt-2 text-[#4C5551]">Hold on a moment — this usually takes a few seconds.</p>
                </article>
            ) : null}

            {state.phase === "paid" && state.data && (
                <article className="ml-card p-6 md:p-8 bg-gradient-to-br from-teal-soft/40 to-cream-light !border-teal-soft" data-testid="booking-status-paid">
                    <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 size={20} strokeWidth={2} className="text-teal-dark" />
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Payment confirmed</p>
                    </div>
                    <h1 className="font-heading text-3xl md:text-4xl text-[#12776F]">
                        You&apos;re booked — pick your time below.
                    </h1>
                    <p className="mt-3 text-[#4C5551] leading-relaxed">
                        Thank you. Your <strong>{state.data.label}</strong> is paid in full
                        (${state.data.amount} USD). Two more things and you&apos;re done:
                    </p>

                    <ol className="mt-6 space-y-5">
                        <li className="ml-card p-5 bg-white" data-testid="booking-step-calendly">
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-teal-dark mb-1">Step 1 — Pick a time</p>
                            <h2 className="font-heading text-xl text-[#1F2624] flex items-center gap-2">
                                <Calendar size={18} strokeWidth={1.8} /> Schedule on Calendly
                            </h2>
                            <p className="mt-1 text-sm text-[#4C5551]">
                                Choose any open slot. You&apos;ll get a calendar invite with the meeting link.
                            </p>
                            {state.data.calendly_url ? (
                                <a
                                    href={state.data.calendly_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="booking-calendly-link"
                                    className="ml-btn-primary !min-h-[48px] mt-4 inline-flex items-center gap-2"
                                >
                                    Open Calendly <ExternalLink size={16} strokeWidth={1.8} />
                                </a>
                            ) : (
                                <p className="mt-3 text-sm text-coral-dark inline-flex items-center gap-2">
                                    <AlertCircle size={14} strokeWidth={1.8} />
                                    Calendly link not configured. We&apos;ll email you within 1 business day.
                                </p>
                            )}
                        </li>

                        <li className="ml-card p-5 bg-white" data-testid="booking-step-doxy">
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-teal-dark mb-1">Step 2 — Day of the call</p>
                            <h2 className="font-heading text-xl text-[#1F2624] flex items-center gap-2">
                                <Video size={18} strokeWidth={1.8} /> Doxy.me video room
                            </h2>
                            <p className="mt-1 text-sm text-[#4C5551]">
                                When it&apos;s session time, click below. It opens in your browser —
                                no app to install. HIPAA-compliant, no recording.
                            </p>
                            {state.data.doxy_room && (
                                <a
                                    href={state.data.doxy_room}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="booking-doxy-link"
                                    className="ml-btn-coral !min-h-[48px] mt-4 inline-flex items-center gap-2"
                                >
                                    Enter video room <ExternalLink size={16} strokeWidth={1.8} />
                                </a>
                            )}
                            <p className="mt-3 text-[11px] text-[#6E7A74] inline-flex items-center gap-1.5">
                                <ShieldCheck size={11} strokeWidth={1.8} /> Bookmark this page or check your email — same link both places.
                            </p>
                        </li>
                    </ol>

                    <p className="mt-6 text-xs text-[#6E7A74]">
                        Receipt: session id {sid.slice(0, 24)}… · Paid via Stripe.
                    </p>
                </article>
            )}

            {(state.phase === "timeout" || state.phase === "expired" || state.phase === "error") && (
                <article className="ml-card p-8 text-center" data-testid="booking-status-issue">
                    <AlertCircle size={32} strokeWidth={1.8} className="mx-auto text-coral-dark" />
                    <h1 className="font-heading text-2xl mt-4 text-[#12776F]">
                        {state.phase === "expired" ? "Checkout expired" : "We couldn't confirm payment yet"}
                    </h1>
                    <p className="mt-2 text-[#4C5551]">
                        {state.phase === "expired"
                            ? "The Stripe checkout session expired before payment finished. You can try again — no charge happened."
                            : "Stripe is still processing. If you saw the success screen, check your email — you'll get the Calendly + Doxy.me links within a couple minutes."}
                    </p>
                    <div className="mt-6 flex gap-3 justify-center">
                        <Link to="/book" className="ml-btn-primary !min-h-[44px]">Try again</Link>
                        <Link to="/navigator" className="ml-btn-coral !min-h-[44px]">Back to Navigator</Link>
                    </div>
                </article>
            )}
        </div>
    );
}
